
import React, { useState } from 'react';
import { useRoleGuard } from '@/hooks/use-role-guard';
import { useStudioStats } from '@/hooks/use-studio-stats';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/studio/DatePickerWithRange';
import { CalendarIcon, BarChart, Users, Clock, TrendingUp, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import StudioOverviewStats from '@/components/studio/insights/StudioOverviewStats';
import ServiceInsights from '@/components/studio/insights/ServiceInsights';
import StaffPerformance from '@/components/studio/insights/StaffPerformance';
import CustomerBehavior from '@/components/studio/insights/CustomerBehavior';
import { useAuth } from '@/contexts/AuthContext';
import { addDays, format } from 'date-fns';
import { DateRange } from 'react-day-picker';

const StudioInsightsPage = () => {
  // Role guard - only studio owners and admins can access
  const { isAuthenticated, userRole } = useRoleGuard(['studio_owner', 'admin']);
  const { user } = useAuth();
  
  // State for filter controls
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });
  
  // Get studio ID dynamically (in a real app)
  const studioId = user?.id; // simplified, would come from a proper studio-user relationship
  
  // Fetch stats with the selected filters
  const { stats, isLoading, error, fetchStudioStats } = useStudioStats({
    period,
    studioId,
    fromDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    toDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined
  });
  
  const handleRefresh = () => {
    fetchStudioStats();
  };
  
  if (isLoading && !stats.appointments) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-chrona-accent" />
          <p className="text-muted-foreground">Lade Statistiken...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-8">
        <Card className="border-red-500/30 bg-red-500/10">
          <CardHeader>
            <CardTitle>Fehler beim Laden der Statistiken</CardTitle>
            <CardDescription>
              Es gab ein Problem beim Abrufen der Statistikdaten.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-red-500">{error.message}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Studio Statistiken & Analysen</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-1"
          >
            <Undo2 className="h-4 w-4" />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* Filter controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Zeitraum & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="w-full sm:w-auto">
              <Select
                value={period}
                onValueChange={(value) => setPeriod(value as any)}
              >
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Zeitraum wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Heute</SelectItem>
                  <SelectItem value="week">Diese Woche</SelectItem>
                  <SelectItem value="month">Letzte 30 Tage</SelectItem>
                  <SelectItem value="year">Dieses Jahr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full">
              <DatePickerWithRange 
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Dashboard tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart className="h-4 w-4" />
            <span>Übersicht</span>
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <span>Leistungen</span>
          </TabsTrigger>
          <TabsTrigger value="staff" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>Mitarbeiter</span>
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>Kundenverhalten</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <StudioOverviewStats stats={stats} isLoading={isLoading} />
        </TabsContent>
        
        <TabsContent value="services">
          <ServiceInsights stats={stats} isLoading={isLoading} />
        </TabsContent>
        
        <TabsContent value="staff">
          <StaffPerformance stats={stats} isLoading={isLoading} />
        </TabsContent>
        
        <TabsContent value="customers">
          <CustomerBehavior stats={stats} isLoading={isLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudioInsightsPage;
